import {
	INVESTOR_TEST_ELEMENT_ID,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { AlertTitle, Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import { CountrySelect as CountrySelectV2 } from '@dealstack/frontend/shared/components/forms/CountrySelect/CountrySelect';
import { PhoneNumberInput } from '@dealstack/frontend/shared/components/forms/PhoneNumberInput/PhoneNumberInput';
import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import {
	CheckboxV2Variant,
	Checkbox as CheckboxV2,
} from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox.v2';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { Divider } from '@dealstack/frontend/shared/components/Pagination/ContextualMenu';
import {
	useTranslationCommonUi,
	useTranslationUserProfiles,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils';
import { Grid, rem } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { CancelConfirmationModal } from '@dealstack/frontend/shared/components/CancelConfirmationModal/CancelConfirmationModal';
import { HeaderBar } from '@dealstack/frontend/shared/components/HeaderBar/HeaderBar';
import {
	FORM_TYPE,
	LEGAL_REPRESENTATIVE_FORM,
	useManagePIVController,
} from '@dealstack/frontend/shared/components/Profile/PIVDetails/ManagePIVDetails/useManagePIVDetailsController';
import { PIV_FORM } from '@dealstack/frontend/admin/pages/Manage/User/ManageInvestorPersonalDetails/useManageInvestorPersonalDetailsController';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';

interface Props {
	className?: string;
}

const ManagePIVDetails = ({ className }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const tUserProfiles = useTranslationUserProfiles();

	const {
		isEditModeEnabled,
		legalRepresentativeForm,
		onLegalRepresentativeSubmit,
		onPIVSubmit,
		setIsEditModeEnabled,
		showModal,
		setShowModal,
		isLoading,
		changeToViewMode,
		user,
		isLegalRepFormDirty,
		FIELD_NAME,
		renderPlaceHolderText,
		isInvestorLegalRepresentativeByDefault,
		changeInvestorAsLegalRepresentative,
		PIVForm,
		isPIVFormDirty,
	} = useManagePIVController();

	const { handleSubmit: handlePivFormSubmit } = PIVForm;
	const { handleSubmit: handleLegalRepresentativeFormSubmit } = legalRepresentativeForm;

	if (isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (!user) {
		return <span>{tUserProfiles('error.no_user')}</span>;
	}

	return (
		<div className={className}>
			<FormProvider {...PIVForm}>
				<form onSubmit={handlePivFormSubmit(onPIVSubmit)}>
					<div>
						<Grid gutter={50}>
							<Grid.Col span={4}>
								<ColumnWrapper>
									<Text variant="sans/11/800" disableVariantMapping>
										{tUserProfiles('piv.basic_information')}
									</Text>

									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_COMPANY_NAME
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivName}
											readOnly={true}
											name={PIV_FORM.PIV_NAME}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_COMPANY_NUMBER
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivRegNumber}
											readOnly={true}
											name={PIV_FORM.PIV_REG_NUMBER}
											inputType="text"
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>
									{isEditModeEnabled[FORM_TYPE.PIV] && (
										<AlertWrapper>
											<Alert state="warning" size="s">
												<AlertTitle>
													{tUserProfiles('piv.alert.title')}
												</AlertTitle>
												<a target="_top" href="mailto:support@dealstack.io">
													<ButtonV2
														variant="secondary"
														buttonTheme="light"
														size="s"
													>
														{tUserProfiles('piv.alert.button_text')}
													</ButtonV2>
												</a>
											</Alert>
										</AlertWrapper>
									)}
								</ColumnWrapper>
							</Grid.Col>
							<Grid.Col span={4}>
								<AddressWrapper>
									<Text variant="sans/11/800" disableVariantMapping>
										{tUserProfiles('personal_details.address')}
									</Text>
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_HOUSE_NUMBER
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivHouseNumber}
											readOnly={!isEditModeEnabled[FORM_TYPE.PIV]}
											name={PIV_FORM.PIV_HOUSE_NUMBER}
											inputType="text"
											inputPlaceHolder={renderPlaceHolderText(
												FIELD_NAME.pivHouseNumber
											)}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>

									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_STREET
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivStreet}
											readOnly={!isEditModeEnabled[FORM_TYPE.PIV]}
											name={PIV_FORM.PIV_STREET}
											inputType="text"
											inputPlaceHolder={renderPlaceHolderText(
												FIELD_NAME.pivStreet
											)}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>

									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_CITY
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivCity}
											readOnly={!isEditModeEnabled[FORM_TYPE.PIV]}
											name={PIV_FORM.PIV_CITY}
											inputType="text"
											inputPlaceHolder={renderPlaceHolderText(
												FIELD_NAME.pivCity
											)}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>

									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_COUNTRY
										}
									>
										<CountrySelectV2
											name={PIV_FORM.PIV_COUNTRY}
											label={FIELD_NAME.pivCountry}
											readOnly={!isEditModeEnabled[FORM_TYPE.PIV]}
											inputType="text"
											inputPlaceHolder={renderPlaceHolderText(
												FIELD_NAME.pivCountry
											)}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>

									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_POSTCODE
										}
									>
										<StringInputV2
											label={FIELD_NAME.pivPostCode}
											readOnly={!isEditModeEnabled[FORM_TYPE.PIV]}
											name={PIV_FORM.PIV_POST_CODE}
											inputType="text"
											inputPlaceHolder={renderPlaceHolderText(
												FIELD_NAME.pivPostCode
											)}
											onInputChange={EMPTY_CALLBACK}
											size="m"
										/>
									</UIElementIdentifier>
								</AddressWrapper>
							</Grid.Col>
						</Grid>
					</div>
					<FieldsDivider />
					{isEditModeEnabled[FORM_TYPE.PIV] && (
						<ButtonWrapper>
							<UIElementIdentifier
								identifier={
									INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_SAVE_BUTTON
								}
							>
								<ButtonV2
									icon="save"
									leftIcon
									size="s"
									variant="primary"
									buttonType="submit"
									buttonTheme="light"
									disabled={!isPIVFormDirty}
								>
									<Text variant="sans/11/600" disableVariantMapping>
										{tCommonUi(`common_buttons.save`)}
									</Text>
								</ButtonV2>
							</UIElementIdentifier>
							<UIElementIdentifier
								identifier={
									INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_CANCEL_BUTTON
								}
							>
								<ButtonV2
									icon="close-v3"
									leftIcon
									size="s"
									variant="tertiary"
									buttonTheme="light"
									onClick={() => {
										if (isPIVFormDirty) {
											setShowModal((prev) => ({
												...prev,
												[FORM_TYPE.PIV]: true,
											}));
										} else {
											changeToViewMode(FORM_TYPE.PIV);
										}
									}}
								>
									<Text variant="sans/11/600" disableVariantMapping>
										{tCommonUi(`common_buttons.cancel`)}
									</Text>
								</ButtonV2>
							</UIElementIdentifier>
						</ButtonWrapper>
					)}
					{!isEditModeEnabled[FORM_TYPE.PIV] && (
						<UIElementIdentifier
							identifier={
								INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_EDIT_BUTTON
							}
						>
							<ButtonV2
								icon="pencil-edit-line"
								leftIcon
								buttonTheme="light"
								size="s"
								variant="tertiary"
								onClick={() =>
									setIsEditModeEnabled((prev) => ({
										...prev,
										[FORM_TYPE.PIV]: true,
									}))
								}
							>
								<Text variant="sans/11/600" disableVariantMapping>
									{tCommonUi(`common_buttons.edit`)}
								</Text>
							</ButtonV2>
						</UIElementIdentifier>
					)}
				</form>
			</FormProvider>
			<HeaderBar
				className={className}
				tabSectionName={tUserProfiles('piv.legal_representative_form.title')}
			></HeaderBar>
			<FormProvider {...legalRepresentativeForm}>
				<form onSubmit={handleLegalRepresentativeFormSubmit(onLegalRepresentativeSubmit)}>
					<LegalRepresentativeSelector>
						<CheckboxV2
							variant={
								legalRepresentativeForm.getValues(
									LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
								)
									? CheckboxV2Variant.on
									: CheckboxV2Variant.off
							}
							size="m"
							onClick={() => {
								legalRepresentativeForm.setValue(
									LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
									!legalRepresentativeForm.watch(
										LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
									)
								);
							}}
						/>
						<Text variant="sans/14/800" disableVariantMapping>
							{tUserProfiles('piv.legal_representative_form.checkbox')}
						</Text>
					</LegalRepresentativeSelector>
					{(!isInvestorLegalRepresentativeByDefault ||
						changeInvestorAsLegalRepresentative ||
						!legalRepresentativeForm.getValues(
							LEGAL_REPRESENTATIVE_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR
						)) && (
						<LegalRepresentativeWrapper>
							<Grid gutter={50}>
								<Grid.Col span={4}>
									<ColumnWrapper>
										<Text variant="sans/11/800" disableVariantMapping>
											{tUserProfiles('personal_details.personal_information')}
										</Text>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_FIRST_NAME
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeFirstName}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_FIRST_NAME
												}
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeFirstName
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_LAST_NAME
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeLastName}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_LAST_NAME
												}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeLastName
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_BIRTHDATE
											}
										>
											<MantineDateInput
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_BIRTH_DATE
												}
												maxDate={new Date()}
												label={FIELD_NAME.representativeBirthDate}
												labelProps={{ className: 'date-input-label' }}
												placeholder={renderPlaceHolderText(
													FIELD_NAME.representativeBirthDate
												)}
												disabled={changeInvestorAsLegalRepresentative}
												size="md"
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
											/>
										</UIElementIdentifier>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_MAIL
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeEmail}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_EMAIL
												}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeEmail
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_PHONE
											}
										>
											<PhoneNumberInput
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_PHONE_NUMBER
												}
												phonePrefixName={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_TELEPHONE_PREFIX
												}
												label={FIELD_NAME.representativePhoneNumber}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>
									</ColumnWrapper>
								</Grid.Col>
								<Grid.Col span={4}>
									<AddressWrapper>
										<Text variant="sans/11/800" disableVariantMapping>
											{tUserProfiles('personal_details.address')}
										</Text>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_HOUSE_NUMBER
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeHouseNumber}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_HOUSE_NUMBER
												}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeHouseNumber
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_STREET
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeStreet}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_STREET
												}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeStreet
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_CITY
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativeCity}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_CITY}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeCity
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_COUNTRY
											}
										>
											<CountrySelectV2
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_COUNTRY
												}
												label={FIELD_NAME.representativeCountry}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												disabled={changeInvestorAsLegalRepresentative}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativeCountry
												)}
												onInputChange={EMPTY_CALLBACK}
												size="m"
											/>
										</UIElementIdentifier>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_POSTALCODE
											}
										>
											<StringInputV2
												label={FIELD_NAME.representativePostCode}
												readOnly={
													!isEditModeEnabled[
														FORM_TYPE.LEGAL_REPRESENTATIVE
													]
												}
												name={
													LEGAL_REPRESENTATIVE_FORM.REPRESENTATIVE_POST_CODE
												}
												inputType="text"
												inputPlaceHolder={renderPlaceHolderText(
													FIELD_NAME.representativePostCode
												)}
												onInputChange={EMPTY_CALLBACK}
												disabled={changeInvestorAsLegalRepresentative}
												size="m"
											/>
										</UIElementIdentifier>
									</AddressWrapper>
								</Grid.Col>
							</Grid>
							<FieldsDivider />
							{isEditModeEnabled[FORM_TYPE.LEGAL_REPRESENTATIVE] && (
								<ButtonWrapper>
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_SAVE_BUTTON
										}
									>
										<ButtonV2
											icon="save"
											leftIcon
											size="s"
											variant="primary"
											buttonType="submit"
											buttonTheme="light"
											disabled={
												changeInvestorAsLegalRepresentative
													? false
													: !isLegalRepFormDirty
											}
										>
											<Text variant="sans/11/600" disableVariantMapping>
												{tCommonUi(`common_buttons.save`)}
											</Text>
										</ButtonV2>
									</UIElementIdentifier>
									<UIElementIdentifier
										identifier={
											INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_CANCEL_BUTTON
										}
									>
										<ButtonV2
											icon="close-v3"
											leftIcon
											size="s"
											variant="tertiary"
											buttonTheme="light"
											onClick={() => {
												if (isLegalRepFormDirty) {
													setShowModal((prev) => ({
														...prev,
														[FORM_TYPE.LEGAL_REPRESENTATIVE]: true,
													}));
												} else {
													changeToViewMode(
														FORM_TYPE.LEGAL_REPRESENTATIVE
													);
												}
											}}
										>
											<Text variant="sans/11/600" disableVariantMapping>
												{tCommonUi(`common_buttons.cancel`)}
											</Text>
										</ButtonV2>
									</UIElementIdentifier>
								</ButtonWrapper>
							)}
							{!isEditModeEnabled[FORM_TYPE.LEGAL_REPRESENTATIVE] && (
								<UIElementIdentifier
									identifier={
										INVESTOR_TEST_ELEMENT_ID.USER_PROFILE_INVESTMENT_VEHICLE_DETAILS_REPRESENTATIVE_EDIT_BUTTON
									}
								>
									<ButtonV2
										icon="pencil-edit-line"
										leftIcon
										buttonTheme="light"
										size="s"
										variant="tertiary"
										onClick={() =>
											setIsEditModeEnabled((prev) => ({
												...prev,
												[FORM_TYPE.LEGAL_REPRESENTATIVE]: true,
											}))
										}
									>
										<Text variant="sans/11/600" disableVariantMapping>
											{tCommonUi(`common_buttons.edit`)}
										</Text>
									</ButtonV2>
								</UIElementIdentifier>
							)}
						</LegalRepresentativeWrapper>
					)}
				</form>
			</FormProvider>
			{showModal[FORM_TYPE.PIV] && (
				<CancelConfirmationModal
					className={className}
					onClose={() => {
						setShowModal((prev) => ({
							...prev,
							[FORM_TYPE.PIV]: false,
						}));
					}}
					discardChange={() => changeToViewMode(FORM_TYPE.PIV)}
					texts={{
						title: tUserProfiles('personal_details.alert.title'),
						description: tUserProfiles('personal_details.alert.description'),
						buttonText: tUserProfiles('personal_details.alert.button_text'),
					}}
				/>
			)}
			{showModal[FORM_TYPE.LEGAL_REPRESENTATIVE] && (
				<CancelConfirmationModal
					className={className}
					onClose={() => {
						setShowModal((prev) => ({
							...prev,
							[FORM_TYPE.LEGAL_REPRESENTATIVE]: false,
						}));
					}}
					discardChange={() => changeToViewMode(FORM_TYPE.LEGAL_REPRESENTATIVE)}
					texts={{
						title: tUserProfiles('personal_details.alert.title'),
						description: tUserProfiles('personal_details.alert.description'),
						buttonText: tUserProfiles('personal_details.alert.button_text'),
					}}
				/>
			)}
		</div>
	);
};
const LegalRepresentativeWrapper = styled.div`
	margin-bottom: ${rem(32)};
`;
const ButtonWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;

const ColumnWrapper = styled.div`
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;
const LegalRepresentativeSelector = styled.div`
	display: flex;
	${CheckboxV2} {
		margin-right: ${rem(8)};
	}
	margin-bottom: ${rem(32)};
	${Text} {
		margin-top: ${rem(2)};
	}
`;
const AddressWrapper = styled.div`
	display: flex;
	gap: 1rem;
	flex-direction: column;
`;
const FieldsDivider = styled(Divider)`
	background-color: ${({ theme }) => theme.v2.color.neutral[200]};
	margin-top: 2rem;
	margin-bottom: 1rem;
`;
const AlertWrapper = styled.div`
	padding: ${rem(16)};
	border-radius: ${rem(8)};
	box-shadow: 0 ${rem(4)} ${rem(8)} 0 ${({ theme }) => theme.v2.color.neutral[500]};
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	${AlertTitle} {
		margin-bottom: ${rem(16)};
	}
`;

const Styled = styled(ManagePIVDetails)`
	.date-input-label {
		font-weight: 400;
	}
	${HeaderBar} {
		margin-top: ${rem(32)};
	}
`;

export { Styled as ManagePIVDetails };
